/**
 * @component Sidebar
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * Based on shadcn/ui + Figma design specification
 */
import { forwardRef, createContext, useContext, ReactNode, HTMLAttributes, ButtonHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { ChevronRight, ChevronDown, Plus, PanelLeftClose, PanelLeft } from 'lucide-react';

// ============ Types ============
export type SidebarGroupLabelVariant = 'base' | 'action' | 'expanded' | 'collapsed';
export type SidebarItemVariant = 'base' | 'expanded' | 'collapsed' | 'dropdown' | 'badge';
export type SidebarItemState = 'default' | 'active' | 'focus' | 'activeFocus';
export type SidebarItemLevel = 1 | 2;

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Collapsed state */
  collapsed?: boolean;
  /** Sidebar width when expanded */
  width?: number | string;
  /** Sidebar width when collapsed */
  collapsedWidth?: number | string;
  children: ReactNode;
}

export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface SidebarContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface SidebarFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface SidebarGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface SidebarGroupLabelProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SidebarGroupLabelVariant;
  /** Show action button (plus icon) */
  showAction?: boolean;
  /** Action button click handler */
  onAction?: () => void;
  /** Collapsible state */
  collapsible?: boolean;
  /** Collapsed state (controlled) */
  isCollapsed?: boolean;
  /** Called when collapse state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  children: ReactNode;
}

export interface SidebarItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Item variant */
  variant?: SidebarItemVariant;
  /** Item state */
  state?: SidebarItemState;
  /** Nesting level */
  level?: SidebarItemLevel;
  /** Icon element */
  icon?: ReactNode;
  /** Badge count */
  badge?: number | string;
  /** Has children (shows expand arrow) */
  hasChildren?: boolean;
  /** Expanded state for items with children */
  expanded?: boolean;
  /** Active/selected state */
  active?: boolean;
  children: ReactNode;
}

export interface SidebarMenuProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export interface SidebarSubMenuProps extends HTMLAttributes<HTMLDivElement> {
  /** Collapsed state */
  collapsed?: boolean;
  children: ReactNode;
}

export interface SidebarToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Collapsed state */
  collapsed?: boolean;
}

export interface SidebarSeparatorProps extends HTMLAttributes<HTMLDivElement> {}

// ============ Context ============
interface SidebarContextValue {
  collapsed: boolean;
}

const SidebarContext = createContext<SidebarContextValue>({ collapsed: false });

export const useSidebar = () => useContext(SidebarContext);

// ============ Sidebar Root ============
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  ({ className, collapsed = false, width = 280, collapsedWidth = 64, children, style, ...props }, ref) => (
    <SidebarContext.Provider value={{ collapsed }}>
      <aside
        ref={ref}
        data-collapsed={collapsed}
        className={cn(
          'flex flex-col h-full bg-card border-r border-accent-2 transition-[width] duration-200 overflow-hidden flex-shrink-0',
          className
        )}
        style={{
          width: collapsed ? collapsedWidth : width,
          ...style,
        }}
        {...props}
      >
        {children}
      </aside>
    </SidebarContext.Provider>
  )
);
Sidebar.displayName = 'Sidebar';

// ============ Sidebar Header ============
export const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 px-4 py-3 border-b border-accent-2', className)}
      {...props}
    >
      {children}
    </div>
  )
);
SidebarHeader.displayName = 'SidebarHeader';

// ============ Sidebar Content ============
export const SidebarContent = forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex-1 overflow-y-auto overflow-x-hidden py-2', className)}
      {...props}
    >
      {children}
    </div>
  )
);
SidebarContent.displayName = 'SidebarContent';

// ============ Sidebar Footer ============
export const SidebarFooter = forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mt-auto border-t border-accent-2 px-4 py-3', className)}
      {...props}
    >
      {children}
    </div>
  )
);
SidebarFooter.displayName = 'SidebarFooter';

// ============ Sidebar Group ============
export const SidebarGroup = forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('py-2', className)}
      {...props}
    >
      {children}
    </div>
  )
);
SidebarGroup.displayName = 'SidebarGroup';

// ============ Sidebar Group Label ============
export const SidebarGroupLabel = forwardRef<HTMLDivElement, SidebarGroupLabelProps>(
  (
    {
      className,
      variant = 'base',
      showAction,
      onAction,
      collapsible,
      isCollapsed,
      onCollapsedChange,
      children,
      ...props
    },
    ref
  ) => {
    const { collapsed: sidebarCollapsed } = useSidebar();

    if (sidebarCollapsed) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider',
          collapsible && 'cursor-pointer hover:text-foreground',
          className
        )}
        onClick={collapsible ? () => onCollapsedChange?.(!isCollapsed) : undefined}
        {...props}
      >
        <span className="truncate">{children}</span>
        <div className="flex items-center gap-1">
          {showAction && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAction?.();
              }}
              className="p-0.5 rounded hover:bg-accent-1 text-muted-foreground hover:text-foreground"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          {collapsible && (
            <span className="text-muted-foreground">
              {isCollapsed ? (
                <ChevronRight className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
            </span>
          )}
        </div>
      </div>
    );
  }
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

// ============ Sidebar Item ============
const sidebarItemVariants = cva(
  'flex items-center gap-2 w-full text-left transition-colors text-sm rounded-md',
  {
    variants: {
      level: {
        1: 'mx-2 px-2 py-2',
        2: 'mx-2 pl-8 pr-2 py-1.5',
      },
      active: {
        true: 'bg-blue-50 text-primary font-medium',
        false: 'text-foreground hover:bg-accent-1',
      },
      collapsed: {
        true: 'justify-center px-2 mx-2',
        false: '',
      },
    },
    compoundVariants: [
      { level: 2, active: true, className: 'bg-blue-50 text-primary font-medium' },
    ],
    defaultVariants: {
      level: 1,
      active: false,
      collapsed: false,
    },
  }
);

export const SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  (
    {
      className,
      variant = 'base',
      level = 1,
      icon,
      badge,
      hasChildren,
      expanded,
      active = false,
      children,
      ...props
    },
    ref
  ) => {
    const { collapsed: sidebarCollapsed } = useSidebar();

    return (
      <button
        ref={ref}
        type="button"
        data-active={active}
        className={cn(
          sidebarItemVariants({ level, active, collapsed: sidebarCollapsed }),
          className
        )}
        {...props}
      >
        {icon && (
          <span className={cn('flex-shrink-0', active ? 'text-primary' : 'text-muted-foreground')}>
            {icon}
          </span>
        )}
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 truncate">{children}</span>
            {badge !== undefined && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-accent-1 text-muted-foreground rounded">
                {badge}
              </span>
            )}
            {hasChildren && (
              <span className="flex-shrink-0 text-muted-foreground">
                {expanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </span>
            )}
          </>
        )}
      </button>
    );
  }
);
SidebarItem.displayName = 'SidebarItem';

// ============ Sidebar Menu ============
export const SidebarMenu = forwardRef<HTMLDivElement, SidebarMenuProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('space-y-0.5', className)}
      {...props}
    >
      {children}
    </div>
  )
);
SidebarMenu.displayName = 'SidebarMenu';

// ============ Sidebar Sub Menu ============
export const SidebarSubMenu = forwardRef<HTMLDivElement, SidebarSubMenuProps>(
  ({ className, collapsed = false, children, ...props }, ref) => {
    if (collapsed) return null;

    return (
      <div
        ref={ref}
        className={cn('space-y-0.5', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarSubMenu.displayName = 'SidebarSubMenu';

// ============ Sidebar Toggle ============
export const SidebarToggle = forwardRef<HTMLButtonElement, SidebarToggleProps>(
  ({ className, collapsed = false, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex items-center justify-center p-2 rounded-md text-muted-foreground hover:bg-accent-1 hover:text-foreground transition-colors',
        className
      )}
      {...props}
    >
      {collapsed ? (
        <PanelLeft className="w-5 h-5" />
      ) : (
        <PanelLeftClose className="w-5 h-5" />
      )}
    </button>
  )
);
SidebarToggle.displayName = 'SidebarToggle';

// ============ Sidebar Separator ============
export const SidebarSeparator = forwardRef<HTMLDivElement, SidebarSeparatorProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('my-2 h-px bg-accent-2 mx-4', className)}
      {...props}
    />
  )
);
SidebarSeparator.displayName = 'SidebarSeparator';
